import { TellescopeModelFromName, useTellescopeInfiniteQuery, useUsersMap } from "@tellescope/tanstack-query";
import { DateRangeFieldValue, useDateFnsFormattersWithLocale, type VirtualizedColumnDef } from "@tellescope/ui";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

type UserLogEntry = TellescopeModelFromName<"user_logs">;

export interface UseActivityTabContentArgs {
  userId?: string;
}

export const useActivityTabContent = ({ userId }: UseActivityTabContentArgs) => {
  const { t } = useTranslation("settings");
  const { format } = useDateFnsFormattersWithLocale();
  const userMap = useUsersMap();

  const [dateRange, setDateRange] = useState<DateRangeFieldValue>({});
  const [recordId, setRecordId] = useState("");

  const logsQuery = useTellescopeInfiniteQuery({
    model: "user_logs",
    method: "getSome",
    request: {
      filter: {
        resourceId: recordId || undefined,
        userId,
      },
      from: dateRange.from !== undefined ? new Date(dateRange.from) : undefined,
      to: dateRange.to !== undefined ? new Date(dateRange.to) : undefined,
    },
    select: (data) => data.pages.flat(),
  });

  const columns = useMemo<VirtualizedColumnDef<UserLogEntry>[]>(() => {
    const actionLabels: Record<UserLogEntry["action"], string> = {
      create: t("Created"),
      update: t("Updated"),
      delete: t("Deleted"),
    };
    return [
      {
        accessorKey: "createdAt",
        label: t("Timestamp"),
        width: "200px",
        cell: ({ row }) => (
          <div className="text-sm truncate">{format(row.original.createdAt, "MMM d, yyyy, h:mm a")}</div>
        ),
      },
      {
        accessorKey: "action",
        label: t("Action"),
        width: "120px",
        cell: ({ row }) => <div className="text-sm truncate">{actionLabels[row.original.action]}</div>,
      },
      {
        accessorKey: "resource",
        label: t("Resources"),
        cell: ({ row }) => (
          <div className="flex flex-col w-[130px]">
            <span className="text-sm truncate">{row.original.resource}</span>
            <span className="text-xs text-muted-foreground truncate">{row.original.resourceId}</span>
          </div>
        ),
      },
      {
        accessorKey: "userId",
        label: t("User"),
        cell: ({ row }) => {
          const user = userMap[row.original.userId];
          const name = user
            ? `${user.fname ?? ""} ${user.lname ?? ""}`.trim() || user.email || user.id
            : row.original.userId;
          return <div className="text-sm truncate">{name}</div>;
        },
      },
    ];
  }, [t, format, userMap]);

  const logs = logsQuery.data ?? [];

  return {
    columns,
    logs,
    dateRange,
    setDateRange,
    recordId,
    setRecordId,
    onLoadMore: () => {
      logsQuery.fetchNextPage();
    },
    hasMoreData: logsQuery.hasNextPage,
    isLoadingMore: logsQuery.isLoading || logsQuery.isFetchingNextPage,
    isEmpty: logsQuery.isFetched && logs.length === 0,
  };
};
